/**
 * Push sounds while a Firmio tab is open.
 *
 * A web push always rings with the operating system's sound. When a tab is
 * open, the service worker (public/sw.js) asks it first: "can you play this
 * one?" If the page answers yes it plays the Firmio sound for that category
 * (lib/sounds.ts) and the notification is shown silently, so there is one
 * sound, ours. If the page cannot (sounds switched off, audio still locked
 * because nobody has clicked yet), it answers no and the system sound plays
 * as before, so a notification is never silent by accident.
 */
import { canPlayNow, playSound, type SoundName } from './sounds';

/** The push payload's `sound` (backend PushSound) → the app's sound. */
const PUSH_TO_SOUND: Record<string, SoundName> = {
  chat: 'message',
  meeting: 'meeting',
  task: 'task',
  voice: 'join',
  knock: 'knock',
};

/**
 * The app's sound for a push `sound`, or null for none. Also used by the
 * desktop app's notifications (hooks/useDesktopNotifications.ts).
 */
export function soundForPush(sound: string | null | undefined): SoundName | null {
  return (sound && PUSH_TO_SOUND[sound]) || null;
}

let listening = false;

export function listenForPushSounds(): void {
  if (listening || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  listening = true;
  navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
    const data = event.data as { type?: unknown; sound?: unknown } | null;
    if (!data || data.type !== 'firmio-push' || typeof data.sound !== 'string') return;
    const name = PUSH_TO_SOUND[data.sound];
    const played = Boolean(name) && canPlayNow();
    if (played) playSound(name);
    event.ports[0]?.postMessage({ played });
  });
}
